import type { Ssm5FreezeFrame } from '@packages/domain/ssm5';

/** A captured moment as a table column. */
export type Ssm5FreezeFrameColumn = {
    /** The captured moment. */
    frame: Ssm5FreezeFrame;
    /** The column's unique key. */
    key: string;
    /** The translation key and sequence of the title. */
    label: Ssm5FreezeFrameColumnLabel;
    /** The position of the frame in each PID's reported values. */
    valueIndex: number;
};

/** The translation key and sequence of a column title. */
export type Ssm5FreezeFrameColumnLabel = {
    /** The translation key. */
    key: string;
    /** The sequence to interpolate for History and Post. */
    sequence?: number;
};

/** How many columns, including the PID column, share the table view's width. */
export const MAX_SHARED_COLUMNS = 6;

/** Each kind's place among the kinds, the direction of its sequence and the translation key of its title. */
const COLUMN_KINDS = {
    detect: { direction: 0, label: 'ssm5:ffd.columns.detect', order: 1 },
    history: { direction: -1, label: 'ssm5:ffd.columns.history', order: 0 },
    post: { direction: 1, label: 'ssm5:ffd.columns.post', order: 2 },
};

/**
 * Order columns as History from the highest sequence, Detect, then Post from the lowest sequence.
 *
 * @param frames - the moments in the order they were reported
 * @returns the columns in design order, pointing to the reported value positions
 */
export function freezeFrameColumns(frames: readonly Ssm5FreezeFrame[]): Ssm5FreezeFrameColumn[] {
    return frames.map(toColumn).sort(compareColumns);
}

/**
 * Give the table its width for a column count that includes the PID column.
 *
 * @param columnCount - the count including the PID column
 * @returns the CSS width, wider than the view above the shared-column limit
 */
export function freezeFrameTableWidth(columnCount: number): string {
    return columnCount <= MAX_SHARED_COLUMNS ? '100%' : `calc(100% * ${columnCount} / ${MAX_SHARED_COLUMNS})`;
}

/**
 * Compare columns by kind order and the kind's sequence direction.
 *
 * @param left - the first column
 * @param right - the second column
 * @returns the sort order
 */
function compareColumns(left: Ssm5FreezeFrameColumn, right: Ssm5FreezeFrameColumn): number {
    const kind = COLUMN_KINDS[left.frame.kind];
    const kindOrder = kind.order - COLUMN_KINDS[right.frame.kind].order;

    if (kindOrder !== 0) {
        return kindOrder;
    }

    return kind.direction * ((left.label.sequence ?? 0) - (right.label.sequence ?? 0));
}

/**
 * Describe a reported frame as a column.
 *
 * @param frame - the captured moment
 * @param valueIndex - its reported position
 * @returns the keyed column and its title
 */
function toColumn(frame: Ssm5FreezeFrame, valueIndex: number): Ssm5FreezeFrameColumn {
    const labelKey = COLUMN_KINDS[frame.kind].label;

    switch (frame.kind) {
        case 'detect':
            return { frame, key: frame.kind, label: { key: labelKey }, valueIndex };
        case 'history':
        case 'post':
            return {
                frame,
                key: `${frame.kind}-${frame.sequence}`,
                label: { key: labelKey, sequence: frame.sequence },
                valueIndex,
            };
    }
}
